import { useCallback, useState } from 'react';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import type { SecretRotationOutcome, SecretRotationResult, WsServerMessage } from '@reflex/shared';

/**
 * One row's worth of rotation progress, keyed by the secret name. The
 * Storage detail page renders these as a virtualised list so an
 * operator can spot which keys failed without scrolling through
 * thousands of healthy ones.
 */
export interface SecretRotationProgressEntry {
  name: string;
  outcome: SecretRotationOutcome;
  /** 1-based index emitted by the server (`processed` field). */
  processed: number;
  /** Total rows the server reports for this pass. */
  total: number;
}

export interface SecretRotationProgressState {
  /** True from the first `progress` event until `complete` arrives. */
  active: boolean;
  /** Latest progress entry; `null` until the first event. */
  latest: SecretRotationProgressEntry | null;
  /** Per-row history, oldest first. Capped to {@link historyLimit}. */
  entries: SecretRotationProgressEntry[];
  /** Final result once `secrets:rotation:complete` is received. */
  result: SecretRotationResult | null;
}

const DEFAULT_HISTORY_LIMIT = 200;

/**
 * Subscribes to the global socket and accumulates per-row progress for
 * an in-flight `POST /admin/secrets/rotate` call. The hook is purely a
 * receiver — kicking off a rotation is the caller's responsibility (they
 * own the toast / mutation). Reset state with {@link reset} before
 * starting a new pass; otherwise the previous result lingers so the UI
 * can keep displaying it.
 *
 * Non-super-admin sessions never receive these messages (the server
 * gates the broadcast on `isSuperAdmin`), so the hook is safe to mount
 * everywhere — it just stays empty.
 */
export function useSecretRotationProgress(opts?: {
  historyLimit?: number;
}): SecretRotationProgressState & { reset: () => void } {
  const historyLimit = opts?.historyLimit ?? DEFAULT_HISTORY_LIMIT;
  const [state, setState] = useState<SecretRotationProgressState>(initial);

  const reset = useCallback(() => {
    setState(initial());
  }, []);

  useSocketMessage((msg: WsServerMessage) => {
    if (msg.type === 'secrets:rotation:progress') {
      setState((prev) => {
        const entry: SecretRotationProgressEntry = {
          name: msg.name,
          outcome: msg.outcome,
          processed: msg.processed,
          total: msg.total,
        };
        const next = prev.entries.concat(entry);
        if (next.length > historyLimit) next.splice(0, next.length - historyLimit);
        return {
          active: true,
          latest: entry,
          entries: next,
          // A second pass starting after a previous completion keeps
          // its old `result` until the server acks the new pass —
          // clearing it here would briefly flash the empty state.
          result: prev.result,
        };
      });
      return;
    }
    if (msg.type === 'secrets:rotation:complete') {
      setState((prev) => ({
        active: false,
        latest: prev.latest,
        entries: prev.entries,
        result: msg.result,
      }));
    }
  });

  return { ...state, reset };
}

function initial(): SecretRotationProgressState {
  return { active: false, latest: null, entries: [], result: null };
}
